<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ApiError } from '../api/client'
import { Button, Field, Input } from '../components/ui'
import { useTitle } from '../lib/hooks'
import { safeInternalPath } from '../lib/paths'
import { useSession } from '../lib/session'

const { status, login } = useSession()
const router = useRouter()
const route = useRoute()
const email = ref('')
const password = ref('')
const error = ref('')
const busy = ref(false)

const from = computed(() => safeInternalPath(route.query.from))

useTitle('登录')

watch(
  status,
  (s) => {
    if (s === 'authenticated') void router.replace(from.value || '/')
  },
  { immediate: true },
)

async function onSubmit(e: Event) {
  e.preventDefault()
  if (busy.value) return
  busy.value = true
  error.value = ''
  try {
    await login(email.value.trim(), password.value)
    await router.replace(from.value || '/')
  } catch (err) {
    if (err instanceof ApiError) {
      error.value = err.code === 'forbidden' ? '该账号没有后台权限' : err.message
    } else {
      error.value = '登录失败'
    }
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
    <section
      class="relative hidden overflow-hidden bg-[var(--color-rail)] px-14 py-14 lg:flex lg:flex-col lg:justify-center"
    >
      <div
        class="pointer-events-none absolute inset-0"
        style="
          background:
            radial-gradient(ellipse 80% 60% at 18% 18%, rgb(13 186 160 / 0.18), transparent 55%),
            radial-gradient(ellipse 50% 40% at 92% 78%, rgb(37 99 235 / 0.14), transparent 50%);
        "
      />
      <div class="relative max-w-md">
        <img src="/logo.png" alt="Reellax" class="h-11 w-auto" />
        <p
          class="mt-8 text-[13px] font-medium tracking-[0.16em] uppercase invisible select-none"
          aria-hidden="true"
        >
          &nbsp;
        </p>
        <h1 class="mt-3 text-[34px] leading-tight font-semibold tracking-tight text-white">
          运营后台
        </h1>
        <p class="mt-4 max-w-sm text-[15px] leading-relaxed text-[var(--color-rail-muted)]">
          管理查看账号权益、审计、内容管理、上架与授权。
        </p>
      </div>
    </section>

    <section class="flex items-center justify-center bg-base px-6 py-12">
      <form class="panel w-full max-w-[380px] px-7 py-8 shadow-[var(--shadow-lift)]" @submit="onSubmit">
        <img src="/logo.png" alt="Reellax" class="mb-6 h-8 w-auto lg:hidden" />
        <p class="text-[12px] font-semibold tracking-[0.08em] text-dim uppercase">欢迎回来</p>
        <h2 class="mt-1.5 mb-7 text-[22px] font-semibold tracking-tight">登录</h2>
        <div class="flex flex-col gap-4">
          <Field label="邮箱" required>
            <Input
              v-model="email"
              name="email"
              type="email"
              autocomplete="username"
              required
            />
          </Field>
          <Field label="密码" required>
            <Input
              v-model="password"
              name="password"
              type="password"
              autocomplete="current-password"
              required
            />
          </Field>
          <p v-if="error" role="alert" class="text-[12.5px] text-danger">{{ error }}</p>
          <Button type="submit" variant="primary" :loading="busy" class="mt-1 w-full">登录</Button>
        </div>
      </form>
    </section>
  </div>
</template>
